import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { hydrateAccount } from '../api/hydrateAccounts'
import { useAccounts } from '../store/accounts'
import { useAiSettings } from '../store/aiSettings'
import {
  buildBashOneLiner,
  buildBatchSignupScript,
  buildExtensionLaunchUrl,
  buildSignupBookmarklet,
  buildSignupScript,
  checkHandleAvailable,
  suggestHandles,
  wrapAsBashCommand,
  wrapAsBat,
} from '../api/handleSuggest'

/**
 * Pick the right OS-shipping format for the downloadable launcher.
 * macOS gets `.command` (Finder double-clicks into Terminal),
 * Windows gets `.bat` (Explorer double-clicks into cmd), Linux gets
 * a `.sh` (chmod required, but explicit instructions in the UI).
 */
function osLauncherFormat(): { ext: string; mime: string; wrap: typeof wrapAsBashCommand } {
  const ua = navigator.userAgent
  if (/Win(dows)?/i.test(ua)) {
    return { ext: 'bat', mime: 'text/plain', wrap: wrapAsBat }
  }
  // macOS + Linux + everything else gets the bash wrapper. macOS
  // detects `.command` specifically; Linux users will get a `.sh`.
  const isMac = /Mac|iPhone|iPad|iPod/i.test(ua)
  return {
    ext: isMac ? 'command' : 'sh',
    mime: 'application/x-sh',
    wrap: wrapAsBashCommand,
  }
}

interface Candidate {
  handle: string
  /** undefined = not checked yet, true = free, false = taken,
   *  'busy' = check in flight, 'err:<msg>' = check failed. */
  state: undefined | true | false | 'busy' | string
}

export function Login() {
  const nav = useNavigate()
  const add = useAccounts((s) => s.add)
  const agents = useAiSettings((s) => s.agents)
  const agent = agents.find((a) => a.enabled) ?? agents[0]

  const [service, setService] = useState('https://bsky.social')
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [authFactor, setAuthFactor] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Name suggestor state — the AI picks creative handles, the human
  // still does captcha + email + password on bsky.app/signup.
  const [schema, setSchema] = useState('word-number')
  const [vibe, setVibe] = useState('')
  const [suggesting, setSuggesting] = useState(false)
  const [suggestErr, setSuggestErr] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<Candidate[]>([])
  // Email-alias generator. Gmail (and many providers) ignores
  // everything after a "+" in the local part, so one inbox can
  // receive mail for `base+N@domain`. Capped at 10 to match the
  // candidate handle count.
  const [emailBase, setEmailBase] = useState('')
  const [emailStart, setEmailStart] = useState(9001)
  // Default so we cover all 10 candidates inclusive (start..start+9).
  const [emailEnd, setEmailEnd] = useState(9010)
  // For display: the EFFECTIVE range is start..start+9 (since
  // candidate count is hard-capped at 10).
  const effectiveEnd = emailStart + 9
  // Password reused across every candidate row's auto-fill
  // bookmarklet. App-passwords come later (Settings → App Passwords
  // on bsky.app); this is the master password Bluesky asks for at
  // signup-time.
  const [signupPw, setSignupPw] = useState('')

  async function generate() {
    if (!agent) {
      setSuggestErr('no AI agent configured — add one in /settings.')
      return
    }
    setSuggesting(true)
    setSuggestErr(null)
    try {
      const handles = await suggestHandles({ agent, schema, vibe })
      setCandidates(handles.map((h) => ({ handle: h, state: undefined })))
      // Fire-and-forget availability check for every one.
      void Promise.all(
        handles.map(async (h) => {
          setCandidates((prev) =>
            prev.map((c) => (c.handle === h ? { ...c, state: 'busy' } : c)),
          )
          const r = await checkHandleAvailable(h)
          setCandidates((prev) =>
            prev.map((c) =>
              c.handle === h
                ? {
                    ...c,
                    state: r.err ? `err:${r.err}` : r.available,
                  }
                : c,
            ),
          )
        }),
      )
    } catch (e) {
      setSuggestErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSuggesting(false)
    }
  }

  async function copy(text: string, alsoFillIdentifier = false) {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      /* clipboard denied — UI won't crash; user can still copy manually */
    }
    if (alsoFillIdentifier) {
      // Pre-fill the identifier field too so they can come back here
      // and fill password after signup.
      setIdentifier(text)
    }
  }

  /**
   * Trigger a browser download of a generated Playwright signup
   * script for the given candidate. Saved as
   * `signup-&lt;handleLocal&gt;.mjs`. The user runs it locally with
   * `node signup-foo.mjs` after a one-time `npm i -D playwright +
   * npx playwright install chromium`.
   */
  /**
   * Open bsky.app in a NEW TAB with a #bskyhq=<base64-json> hash.
   * If the operator has the helper extension installed, its content
   * script reads the hash and drives the form to the captcha. If
   * not, they just see bsky.app's home — no harm done, but they'll
   * need to install the extension first.
   */
  function launchViaExtension(handle: string, email: string | null) {
    if (!signupPw) {
      // eslint-disable-next-line no-alert
      alert('set a signup password first.')
      return
    }
    if (!email) {
      // eslint-disable-next-line no-alert
      alert('set an email base + range first.')
      return
    }
    const url = buildExtensionLaunchUrl({
      email,
      password: signupPw,
      handle,
    })
    window.open(url, '_blank', 'noopener')
  }

  /**
   * Copy a single-line bash command to the operator's clipboard.
   * They paste into Terminal, hit Enter — Playwright fetches
   * + chromium opens. Sidesteps macOS Gatekeeper (no file =
   * no quarantine) and the missing-exec-bit problem.
   */
  async function copyLaunchCommand(handle: string, email: string | null) {
    if (!signupPw) {
      // eslint-disable-next-line no-alert
      alert('set a signup password first.')
      return
    }
    if (!email) {
      // eslint-disable-next-line no-alert
      alert('set an email base + range first.')
      return
    }
    const scriptBody = buildSignupScript({
      email,
      password: signupPw,
      handle,
    })
    const cmd = buildBashOneLiner(scriptBody)
    try {
      await navigator.clipboard.writeText(cmd)
      // eslint-disable-next-line no-alert
      alert(
        '✓ command copied. Open Terminal (⌘+Space → "Terminal") and paste (⌘+V), then hit Enter.\n\nFirst run installs Playwright (~1 min), then Chromium opens with email/password/handle filled. You solve the captcha + click Submit.',
      )
    } catch {
      // eslint-disable-next-line no-alert
      prompt('Copy this command into Terminal:', cmd)
    }
  }

  async function copyBatchLaunchCommand() {
    if (!signupPw) {
      // eslint-disable-next-line no-alert
      alert('set a signup password first.')
      return
    }
    const list: { email: string; password: string; handle: string }[] = []
    for (let i = 0; i < candidates.length; i++) {
      const e = emailForIndex(i)
      if (!e) {
        // eslint-disable-next-line no-alert
        alert('set an email base first.')
        return
      }
      list.push({ email: e, password: signupPw, handle: candidates[i].handle })
    }
    const scriptBody = buildBatchSignupScript({ candidates: list })
    const cmd = buildBashOneLiner(scriptBody)
    try {
      await navigator.clipboard.writeText(cmd)
      // eslint-disable-next-line no-alert
      alert(
        `✓ batch command (${candidates.length} signups) copied. Paste into Terminal + Enter. Solve a captcha per signup; close each browser to advance to the next.`,
      )
    } catch {
      // eslint-disable-next-line no-alert
      prompt('Copy this command into Terminal:', cmd)
    }
  }

  function downloadScript(handle: string, email: string | null) {
    if (!signupPw) {
      // eslint-disable-next-line no-alert
      alert(
        'set a signup password first (or click ✦ random next to the password field). it gets baked into the downloaded script.',
      )
      return
    }
    if (!email) {
      // eslint-disable-next-line no-alert
      alert('set an email base + range first so the script has an email to use.')
      return
    }
    const handleLocal = handle.replace(/\.bsky\.social$/i, '')
    const scriptBody = buildSignupScript({
      email,
      password: signupPw,
      handle,
    })
    const fmt = osLauncherFormat()
    const wrapped = fmt.wrap(scriptBody)
    const blob = new Blob([wrapped], { type: fmt.mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `signup-${handleLocal}.${fmt.ext}`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  /**
   * Pack every candidate into one Playwright script that runs all
   * signups SERIALLY — fresh chromium.launch() per signup, no
   * persistent state inherited (= "signed out"). The operator solves
   * one captcha per browser window, closes the window when done, and
   * the script auto-advances to the next candidate.
   */
  function downloadBatch() {
    if (!signupPw) {
      // eslint-disable-next-line no-alert
      alert('set a signup password (or click ✦ random) — it bakes into every entry.')
      return
    }
    const list: { email: string; password: string; handle: string }[] = []
    for (let i = 0; i < candidates.length; i++) {
      const email = emailForIndex(i)
      if (!email) {
        // eslint-disable-next-line no-alert
        alert('set an email base + range first so every candidate has an email.')
        return
      }
      list.push({ email, password: signupPw, handle: candidates[i].handle })
    }
    const scriptBody = buildBatchSignupScript({ candidates: list })
    const fmt = osLauncherFormat()
    const wrapped = fmt.wrap(scriptBody)
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
    const blob = new Blob([wrapped], { type: fmt.mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `signup-batch-${ts}.${fmt.ext}`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  /**
   * Build base+N@domain for a given index. Returns null when the
   * base isn't a valid `local@domain` shape, so the UI can hide
   * email columns until the operator types something usable.
   */
  function emailForIndex(idx: number): string | null {
    const base = emailBase.trim()
    if (!base.includes('@')) return null
    const [local, domain] = base.split('@')
    if (!local || !domain) return null
    // Always emit start + idx — the typed `end` is informational
    // only. Candidate list is capped at 10 elsewhere, so we never
    // emit more than 10 aliases here.
    const offset = emailStart + idx
    return `${local}+${offset}@${domain}`
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      const sess = await api.login({
        service,
        identifier: identifier.trim(),
        password,
        authFactorToken: authFactor.trim() || undefined,
      })
      add({
        id: sess.did,
        did: sess.did,
        handle: sess.handle,
        service,
        accessJwt: sess.accessJwt,
        refreshJwt: sess.refreshJwt,
        displayName: null,
        avatar: null,
        addedAt: Date.now(),
      })
      // Fire-and-forget profile hydration so the rail shows the avatar
      // shortly after login without blocking nav.
      void hydrateAccount(sess.did)
      nav('/')
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto max-w-md space-y-3 p-4">
      {/* Browser extension — the only TRULY click-and-done path.
          Install once, then 🧩 launch on any candidate row opens a
          new tab in this same browser and the extension fills the
          form. No Node, no Terminal, no .command files. */}
      <section className="glass-tinted space-y-3 p-4">
        <h2 className="text-base font-bold">🧩 install browser extension</h2>
        <p className="text-[11px] text-ink-300">
          One-time install: lets the{' '}
          <span className="rounded border border-sky-400/40 bg-sky-500/15 px-1 text-sky-100">
            🧩 launch
          </span>{' '}
          button below open a new tab in <em>this</em> browser and fill
          bsky.app's signup form. No Node, no Terminal, no Gatekeeper.
        </p>
        <ol className="list-decimal space-y-1 pl-5 text-[11px] text-ink-300">
          <li>
            <a
              href="/bskyhq-helper.zip"
              download
              className="rounded-full border border-sky-400/30 bg-sky-500/15 px-2 py-0.5 text-sky-100 hover:bg-sky-500/25"
            >
              📦 download bskyhq-helper.zip
            </a>{' '}
            and unzip it (Finder / Explorer creates a{' '}
            <code className="rounded bg-white/10 px-1">bskyhq-helper</code>{' '}
            folder).
          </li>
          <li>
            Open{' '}
            <code className="rounded bg-white/10 px-1">
              chrome://extensions
            </code>{' '}
            (or{' '}
            <code className="rounded bg-white/10 px-1">edge://extensions</code>
            ,{' '}
            <code className="rounded bg-white/10 px-1">brave://extensions</code>
            ) — toggle <strong>Developer mode</strong> on (top right).
          </li>
          <li>
            Click <strong>Load unpacked</strong> → pick the unzipped{' '}
            <code className="rounded bg-white/10 px-1">bskyhq-helper</code>{' '}
            folder.
          </li>
          <li>
            Done. Use{' '}
            <span className="rounded border border-sky-400/40 bg-sky-500/15 px-1 text-sky-100">
              🧩 launch
            </span>{' '}
            on any candidate below — opens bsky.app in a new tab, fills
            Steps 1+2, leaves the captcha for you. Firefox/Safari support
            coming later.
          </li>
        </ol>
      </section>

      {/* Name suggestor — AI picks the creative handle, human does the
          captcha + email + password on bsky.app/signup. */}
      <section className="glass-tinted space-y-3 p-4">
        <h2 className="text-base font-bold">✦ name suggestor</h2>
        <p className="text-[11px] text-ink-300">
          The AI proposes 10 fresh handles. We probe Bluesky's public
          AppView for each so you can see which are free. You still create
          the account on bsky.app (captcha + email + password). Once it's
          live, paste the handle below + your app password to add it here.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <label className="flex flex-1 flex-col gap-1 text-[11px] uppercase tracking-widest text-ink-400">
            schema
            <select
              className="glass-input"
              value={schema}
              onChange={(e) => setSchema(e.target.value)}
            >
              <option value="word-number">word + 3-digit number</option>
              <option value="x-y-z">x-y-z (3 single letters)</option>
              <option value="two-words">two-words</option>
              <option value="free">free / anything memorable</option>
            </select>
          </label>
          <label className="flex flex-1 flex-col gap-1 text-[11px] uppercase tracking-widest text-ink-400">
            vibe (optional)
            <input
              className="glass-input"
              value={vibe}
              onChange={(e) => setVibe(e.target.value)}
              placeholder="e.g. dark academia / cottagecore / cyberpunk"
            />
          </label>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/5 p-2">
          <div className="mb-1 text-[10px] uppercase tracking-widest text-ink-400">
            email aliases (optional)
          </div>
          <p className="mb-2 text-[10px] text-ink-400">
            Gmail ignores everything after a "+" in the local part, so one
            inbox can receive mail for <code>base+N@domain</code>. Pair each
            candidate with a unique alias.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-1 flex-col gap-1 text-[10px] uppercase tracking-widest text-ink-400">
              base
              <input
                type="email"
                className="glass-input"
                value={emailBase}
                onChange={(e) => setEmailBase(e.target.value)}
                placeholder="tim.l.nelson@gmail.com"
                autoComplete="off"
              />
            </label>
            <label className="flex w-24 flex-col gap-1 text-[10px] uppercase tracking-widest text-ink-400">
              start
              <input
                type="number"
                className="glass-input text-right tabular-nums"
                value={emailStart}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  if (!Number.isNaN(n)) setEmailStart(n)
                }}
              />
            </label>
            <label className="flex w-24 flex-col gap-1 text-[10px] uppercase tracking-widest text-ink-400">
              end
              <input
                type="number"
                className="glass-input text-right tabular-nums"
                value={emailEnd}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  if (!Number.isNaN(n)) setEmailEnd(n)
                }}
              />
            </label>
          </div>
          <div className="mt-1 text-[10px] text-ink-400">
            uses <strong className="text-ink-200">{emailStart}–{effectiveEnd}</strong>{' '}
            (one alias per candidate, max 10).
            {emailEnd !== effectiveEnd ? (
              <span className="ml-1 text-amber-200">
                (typed end {emailEnd} is informational only)
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="flex flex-1 flex-col gap-1 text-[10px] uppercase tracking-widest text-ink-400">
              signup password (Bluesky master password)
              <input
                type="text"
                className="glass-input"
                value={signupPw}
                onChange={(e) => setSignupPw(e.target.value)}
                placeholder="paste or type — used by every row's auto-fill bookmarklet"
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              onClick={() => {
                // Generate a passphrase: 4 short random words +
                // "!" + 4-digit number. Easy to remember, hard to
                // guess. Operator can edit after.
                const words = [
                  'amber','tide','quartz','ember','opal','silver','glass',
                  'cobalt','copper','onyx','frost','willow','indigo','ivory',
                  'jade','linen','olive','pearl','rust','umber','wander',
                ]
                const pick = () => words[Math.floor(Math.random() * words.length)]
                const n = Math.floor(1000 + Math.random() * 9000)
                setSignupPw(`${pick()}-${pick()}-${pick()}!${n}`)
              }}
              className="glass-button text-xs"
              title="generate a memorable passphrase"
            >
              ✦ random
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void generate()}
            disabled={!agent || suggesting}
            className="glass-button text-xs font-bold"
            title={
              agent
                ? `ask ${agent.name} for 10 candidates`
                : 'configure an AI agent in /settings first'
            }
          >
            {suggesting ? '✦ thinking…' : '✦ suggest 10'}
          </button>
          {!agent ? (
            <span className="text-[10px] text-amber-200">
              add an AI agent in /settings to use this
            </span>
          ) : null}
        </div>
        <SignupSteps />
        {suggestErr ? (
          <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
            {suggestErr}
          </div>
        ) : null}
        {candidates.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-ink-300">
              {candidates.length} candidates
            </span>
            <button
              type="button"
              onClick={() => void copyBatchLaunchCommand()}
              disabled={!signupPw || !emailBase}
              className="ml-auto rounded-md border border-emerald-400/50 bg-emerald-500/20 px-2 py-0.5 text-[11px] font-bold text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40"
              title="copy a single-line bash command for all candidates serially. paste in Terminal + Enter. Each candidate opens a fresh signed-out Chromium, fills Step 1+2, pauses on the captcha; close the window to advance to the next candidate. NO file download = NO Gatekeeper headaches."
            >
              📋 launch all {candidates.length} (serial)
            </button>
            <button
              type="button"
              onClick={downloadBatch}
              disabled={!signupPw || !emailBase}
              className="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-ink-300 hover:bg-white/10 disabled:opacity-40"
              title="alternative: download a .command/.bat file. macOS will need a chmod + Gatekeeper bypass; prefer 📋 launch all."
            >
              📥 file
            </button>
          </div>
        ) : null}
        {candidates.length > 0 ? (
          <ul className="space-y-1 text-xs">
            {candidates.map((c, idx) => {
              const email = emailForIndex(idx)
              return (
                <li
                  key={c.handle}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2 py-1"
                >
                  <span className="flex-1 min-w-0 truncate font-mono text-ink-100">
                    @{c.handle}
                  </span>
                  <span className="w-20 shrink-0 text-right text-[10px]">
                    {c.state === undefined ? (
                      <span className="text-ink-400">…</span>
                    ) : c.state === 'busy' ? (
                      <span className="text-ink-300">checking</span>
                    ) : c.state === true ? (
                      <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0.5 text-emerald-100">
                        ✓ free
                      </span>
                    ) : c.state === false ? (
                      <span className="rounded-full border border-rose-400/30 bg-rose-500/10 px-1.5 py-0.5 text-rose-100">
                        ✕ taken
                      </span>
                    ) : (
                      <span className="text-amber-200" title={String(c.state)}>
                        err
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => void copy(c.handle, true)}
                    className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-ink-200 hover:bg-white/10"
                    title="copy handle + pre-fill the sign-in form below"
                  >
                    copy ✎
                  </button>
                  {email ? (
                    <>
                      <span className="w-full sm:w-auto sm:flex-1 truncate font-mono text-[11px] text-sky-200">
                        {email}
                      </span>
                      <button
                        type="button"
                        onClick={() => void copy(email)}
                        className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-ink-200 hover:bg-white/10"
                        title="copy email alias to clipboard"
                      >
                        copy ✉
                      </button>
                    </>
                  ) : null}
                  <a
                    href={buildSignupBookmarklet({
                      handle: c.handle,
                      email: email ?? undefined,
                      password: signupPw,
                    })}
                    onClick={(e) => {
                      e.preventDefault()
                      alert(
                        'Drag this link to your bookmarks bar (in the private window where bsky.app signup is open), then click the bookmark on bsky.app Step 1 or 2 — fields autofill.',
                      )
                    }}
                    draggable
                    className="rounded-md border border-amber-400/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-100 hover:bg-amber-500/20"
                    title="DRAG this link to your private-window bookmarks bar. Then click it on bsky.app's Step 1 / Step 2 of 3 — fills email + password + handle automatically. Captcha (Step 3) is human-only."
                  >
                    ↗ fill
                  </a>
                  <button
                    type="button"
                    onClick={() => launchViaExtension(c.handle, email)}
                    className="rounded-md border border-sky-400/50 bg-sky-500/20 px-2 py-0.5 text-[10px] font-bold text-sky-100 hover:bg-sky-500/30"
                    title="🧩 EXTENSION PATH (recommended): opens bsky.app in a new tab in THIS browser; the helper extension fills the form. No Node, no Terminal, no Gatekeeper. Install the extension first via the card at the top of this page."
                  >
                    🧩 launch
                  </button>
                  <button
                    type="button"
                    onClick={() => void copyLaunchCommand(c.handle, email)}
                    className="rounded-md border border-emerald-400/40 bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-100 hover:bg-emerald-500/25"
                    title="📋 TERMINAL PATH: copies a single-line bash command. Paste in Terminal + Enter. First run installs Playwright; subsequent runs open Chromium instantly. Use this if you can't / don't want to install the extension."
                  >
                    📋 cmd
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadScript(c.handle, email)}
                    className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-ink-300 hover:bg-white/10"
                    title="Alternative: downloads a double-clickable .command (macOS) / .bat (Windows) file. macOS users may hit Gatekeeper + chmod issues — prefer 📋 launch on Mac."
                  >
                    📥 file
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}
      </section>

      <form onSubmit={submit} className="glass space-y-3 p-4">
        <h2 className="text-base font-bold">add an account</h2>
        <p className="text-[11px] text-ink-400">
          App passwords are recommended (Settings → App Passwords on bsky.app). The
          worker proxies the login to your PDS — tokens are stored in your browser
          only.
        </p>
        <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
          service
          <input
            className="glass-input"
            value={service}
            onChange={(e) => setService(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
          handle or did
          <input
            className="glass-input"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="alice.bsky.social"
            required
          />
        </label>
        <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
          password / app password
          <input
            type="password"
            className="glass-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
          2fa code (optional)
          <input
            className="glass-input"
            value={authFactor}
            onChange={(e) => setAuthFactor(e.target.value)}
          />
        </label>
        {err ? (
          <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
            {err}
          </div>
        ) : null}
        <button type="submit" className="glass-button" disabled={busy}>
          {busy ? 'signing in…' : 'sign in'}
        </button>
      </form>
    </main>
  )
}

/**
 * Signup helper. We can't actually embed Bluesky's signup in our
 * app — three independent blockers stack up:
 *   1. bsky.app sets X-Frame-Options: SAMEORIGIN, so iframing
 *      from our origin is blocked at the browser level.
 *   2. Bluesky's hCaptcha site key is bound to bsky.app/bsky.social;
 *      a token rendered from any other origin is rejected.
 *   3. Bluesky has no deep link to the "Step 1 of 3" modal — it
 *      only renders when bsky.app's root sees an unauthenticated
 *      session.
 * Plus: browsers deliberately have no JS API for opening private
 * windows. So the best honest UX is: one button that pops a sized
 * window to bsky.app/, a fallback for the private-window path, and
 * a short "why isn't this seamless?" disclosure.
 */
function SignupSteps() {
  const detected = detectIncognitoShortcut()
  const [whyOpen, setWhyOpen] = useState(false)
  async function copyUrl() {
    try {
      await navigator.clipboard.writeText('https://bsky.app/')
    } catch {
      /* clipboard denied — UI won't crash */
    }
  }
  function openSignupPopup() {
    // Sized popup feels more focused than a full tab. If the user
    // is already signed in to bsky.app they'll land on home — the
    // private-window step below is the unconditional fallback.
    window.open(
      'https://bsky.app/',
      'bskyhqSignup',
      'width=520,height=900,popup=yes,noopener',
    )
  }
  return (
    <div className="space-y-2 rounded-lg border border-white/10 bg-white/5 p-2 text-[11px] text-ink-200">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={openSignupPopup}
          className="glass-button text-xs font-bold"
          title="open bsky.app in a popup window"
        >
          ▶ open signup popup
        </button>
        <span className="text-ink-300">— works if you're signed OUT.</span>
        <button
          type="button"
          onClick={() => setWhyOpen((v) => !v)}
          className="ml-auto rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-ink-300 hover:bg-white/10"
        >
          {whyOpen ? '▾ why' : '▸ why no one-click signup?'}
        </button>
      </div>
      {whyOpen ? (
        <ul className="list-disc rounded-md border border-white/10 bg-white/[0.03] p-2 pl-5 text-[10px] text-ink-300">
          <li>
            <strong>X-Frame-Options:</strong> bsky.app refuses to render in an
            iframe outside its own origin (browser-level block).
          </li>
          <li>
            <strong>Captcha origin lock:</strong> Bluesky's hCaptcha site key is
            tied to bsky.app/bsky.social. A token from any other origin gets
            rejected by their PDS, so we can't embed the captcha + call{' '}
            <code>createAccount</code> ourselves.
          </li>
          <li>
            <strong>No deep link:</strong> Bluesky's signup is a modal at the
            root, not a routable URL — the modal only renders for signed-out
            visitors.
          </li>
          <li>
            <strong>Browser security:</strong> JS can't open private/incognito
            windows; that's deliberate.
          </li>
        </ul>
      ) : null}
      <div className="text-ink-400">
        <strong className="text-ink-200">If the popup lands on home</strong>{' '}
        instead of the signup modal, you're signed in to bsky.app already.
        Open a <strong>private / incognito window</strong> —{' '}
        <kbd className="rounded border border-white/15 bg-white/10 px-1 py-0.5 text-[10px] text-white">
          {detected.shortcut}
        </kbd>
        {detected.note ? <span> ({detected.note})</span> : null} — and paste{' '}
        <code className="rounded bg-white/10 px-1 py-0.5">https://bsky.app/</code>{' '}
        <button
          type="button"
          onClick={() => void copyUrl()}
          className="ml-1 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-ink-200 hover:bg-white/10"
        >
          copy URL
        </button>{' '}
        — Bluesky's "Step 1 of 3" renders automatically for signed-out
        visitors.
      </div>
      <div className="text-ink-400">
        Four paths, ranked by laziness:
      </div>
      <ul className="list-disc space-y-1 pl-5 text-ink-400">
        <li>
          <span className="rounded border border-sky-400/50 bg-sky-500/20 px-1 text-sky-100">
            🧩 launch
          </span>{' '}
          (per row) — extension path. <strong>Recommended.</strong> One-time
          install of the helper extension (card at top of page). After that,
          click → opens a new tab in THIS browser → extension fills Steps
          1+2 → captcha is yours. No Node, no Terminal, no files.
        </li>
        <li>
          <span className="rounded border border-emerald-400/40 bg-emerald-500/15 px-1 text-emerald-100">
            📋 cmd
          </span>{' '}
          /{' '}
          <span className="rounded border border-emerald-400/40 bg-emerald-500/15 px-1 text-emerald-100">
            📋 launch all 10
          </span>{' '}
          — terminal path. Copies a single-line bash command. Paste in
          Terminal + Enter. First run auto-installs Playwright into{' '}
          <code className="rounded bg-white/10 px-1 py-0.5">
            ~/.bskyhq-signup
          </code>
          ; subsequent runs open Chromium instantly. Use if extension
          install isn't an option.
        </li>
        <li>
          <span className="rounded border border-white/10 bg-white/5 px-1 text-ink-200">
            📥 file
          </span>{' '}
          — double-clickable launcher (<code className="rounded bg-white/10 px-1 py-0.5">.command</code>{' '}
          mac /{' '}
          <code className="rounded bg-white/10 px-1 py-0.5">.bat</code> win
          /{' '}
          <code className="rounded bg-white/10 px-1 py-0.5">.sh</code> linux).
          macOS may complain about quarantine + missing exec bit.
        </li>
        <li>
          <span className="rounded border border-amber-400/30 bg-amber-500/10 px-1 text-amber-100">
            ↗ fill
          </span>{' '}
          — bookmarklet. No install of anything: drag to bookmarks bar,
          click on bsky.app/ — drives Steps 1+2 in your existing browser.
        </li>
      </ul>
    </div>
  )
}

/** Best-guess incognito shortcut for the operator's browser + OS.
 *  Defaults to Cmd/Ctrl-Shift-N (Chrome/Edge) which covers the
 *  majority. Firefox and Safari use Shift-P instead. */
function detectIncognitoShortcut(): { shortcut: string; note?: string } {
  const ua = navigator.userAgent
  const isMac = /Mac|iPhone|iPad|iPod/.test(ua)
  const mod = isMac ? '⌘' : 'Ctrl'
  if (/Firefox/i.test(ua)) {
    return { shortcut: `${mod}+Shift+P`, note: 'Firefox: "New Private Window"' }
  }
  if (/Safari/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua)) {
    return { shortcut: `${mod}+Shift+N`, note: 'Safari: "New Private Window"' }
  }
  // Chrome / Edge / Brave / Opera / Arc — all Chromium.
  return { shortcut: `${mod}+Shift+N`, note: 'Chrome / Edge: "New Incognito Window"' }
}
